import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useExplored,
  useMapLocations,
  useMapPositions,
  useClearTravelTarget,
  useSetTravelTarget,
  usePlayerSkills,
} from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { worldMapImageUrl, imageUrl } from '@/api/client';
import { cn } from '@/lib/cn';
import { useMapRenderer, type MapState } from './useMapRenderer';
import { LocationToast } from './LocationToast';
import { TravelTargetBar } from './TravelTargetBar';
import { ZoomControls } from './ZoomControls';
import { clamp } from '@/lib/format';
import { hapticNotify } from '@/lib/telegram';
import type { MapLocation } from '@/types';

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 1.0;

interface WorldMapProps {
  className?: string;
}

export function WorldMap({ className }: WorldMapProps) {
  const userId = useAuthStore((s) => s.userId);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mapImageRef = useRef<HTMLImageElement | null>(null);
  const avatarsRef = useRef<Map<number, HTMLImageElement>>(new Map());

  const stateRef = useRef<MapState>({
    zoom: 0.6,
    pan: { x: 0, y: 0 },
    selectedId: null,
    travelTarget: null,
    currentPlayerId: userId,
  });

  const [, forceRender] = useState(0);
  const repaint = useCallback(() => forceRender((v) => v + 1), []);
  const [mapReady, setMapReady] = useState(false);

  const { scheduleDraw, invalidateFog } = useMapRenderer();

  const { data: locationsData } = useMapLocations();
  const { data: positionsData } = useMapPositions(!!userId);
  const { data: exploredData } = useExplored(userId);
  const { data: playerSkills } = usePlayerSkills(userId);

  const locations = useMemo(() => locationsData?.locations ?? [], [locationsData]);
  const positions = useMemo(() => positionsData?.positions ?? [], [positionsData]);
  const explored = useMemo(
    () => new Set(exploredData?.explored_locations ?? []),
    [exploredData],
  );

  const navigatorUnlocked = (playerSkills?.skills?.navigator ?? 0) > 0;

  const setTravelTarget = useSetTravelTarget(userId ?? 0);
  const clearTravelTarget = useClearTravelTarget(userId ?? 0);

  // Update currentPlayerId / travelTarget when positions change
  useEffect(() => {
    stateRef.current.currentPlayerId = userId;
    const me = positions.find((p) => p.user_id === userId);
    if (me) {
      stateRef.current.travelTarget = me.travel_target ?? null;
    }
    scheduleDrawNow();
  }, [userId, positions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Invalidate fog cache when explored set changes
  useEffect(() => {
    invalidateFog();
    scheduleDrawNow();
  }, [exploredData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load map image once
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Only flag readiness here; the draw/center effects (keyed on mapReady)
      // run with fresh closures over the latest locations/positions/explored.
      mapImageRef.current = img;
      setMapReady(true);
    };
    img.onerror = () => {
      console.error('[WorldMap] failed to load map image');
    };
    img.src = worldMapImageUrl(Date.now());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resize canvas to container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas || !container) return;
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.floor(rect.width);
      const h = Math.floor(rect.height);
      if (canvas.width === w * dpr && canvas.height === h * dpr) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      scheduleDrawNow();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    window.addEventListener('resize', resize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load avatars for players we don't yet have
  useEffect(() => {
    positions.forEach((player) => {
      if (!player.avatar_path) return;
      if (avatarsRef.current.has(player.user_id)) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => scheduleDrawNow();
      img.src = imageUrl(player.avatar_path) ?? '';
      avatarsRef.current.set(player.user_id, img);
    });
    scheduleDrawNow();
  }, [positions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial center on player once map image, locations and the player's
  // position are all available. Only lock the flag once centering succeeds.
  const didCenterRef = useRef(false);
  useEffect(() => {
    if (didCenterRef.current) return;
    if (!mapReady || !mapImageRef.current || locations.length === 0 || !userId) return;
    const me = positions.find((p) => p.user_id === userId);
    if (!me?.current_location_id) return;
    if (centerOnPlayer(true)) didCenterRef.current = true;
  }, [positions, locations, userId, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  function scheduleDrawNow() {
    const canvas = canvasRef.current;
    const mapImage = mapImageRef.current;
    const container = containerRef.current;
    if (!canvas || !mapImage || !container) return;
    const rect = container.getBoundingClientRect();
    scheduleDraw({
      canvas,
      mapImage,
      state: stateRef.current,
      data: { locations, positions, explored },
      avatars: avatarsRef.current,
      viewportW: rect.width,
      viewportH: rect.height,
    });
  }

  function centerOnPlayer(force = false): boolean {
    const mapImage = mapImageRef.current;
    if (!mapImage) return false;
    const me = positions.find((p) => p.user_id === userId);
    if (!me?.current_location_id && !force) return false;
    const loc = locations.find((l) => l.id === me?.current_location_id);
    if (!loc) return false;
    const mapW = mapImage.naturalWidth || 1024;
    const mapH = mapImage.naturalHeight || 1024;
    const targetX = (loc.x / 100) * mapW;
    const targetY = (loc.y / 100) * mapH;
    stateRef.current.pan = {
      x: mapW / 2 - targetX,
      y: mapH / 2 - targetY,
    };
    scheduleDrawNow();
    return true;
  }

  // Repaint when data changes or the map image becomes ready
  useEffect(() => {
    scheduleDrawNow();
  }, [locations, positions, explored, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Touch / mouse pan
  const dragRef = useRef<{ x: number; y: number; active: boolean }>({ x: 0, y: 0, active: false });

  const onDown = (clientX: number, clientY: number) => {
    dragRef.current = { x: clientX, y: clientY, active: true };
  };

  const onMove = (clientX: number, clientY: number) => {
    if (!dragRef.current.active) return;
    const dx = (clientX - dragRef.current.x) / stateRef.current.zoom;
    const dy = (clientY - dragRef.current.y) / stateRef.current.zoom;
    stateRef.current.pan.x += dx;
    stateRef.current.pan.y += dy;
    dragRef.current.x = clientX;
    dragRef.current.y = clientY;
    scheduleDrawNow();
  };

  const onUp = () => {
    dragRef.current.active = false;
  };

  // Click on canvas -> hit-test locations
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const mapImage = mapImageRef.current;
    if (!canvas || !mapImage) return;
    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;
    const mapW = mapImage.naturalWidth || 1024;
    const mapH = mapImage.naturalHeight || 1024;
    const mapX =
      (clickX - w / 2) / stateRef.current.zoom - stateRef.current.pan.x + mapW / 2;
    const mapY =
      (clickY - h / 2) / stateRef.current.zoom - stateRef.current.pan.y + mapH / 2;
    let closest: MapLocation | null = null;
    let minDist = 30; // hit radius in map pixels
    locations.forEach((loc) => {
      const lx = (loc.x / 100) * mapW;
      const ly = (loc.y / 100) * mapH;
      const d = Math.hypot(mapX - lx, mapY - ly);
      if (d < minDist) {
        minDist = d;
        closest = loc;
      }
    });
    if (closest) {
      stateRef.current.selectedId = (closest as MapLocation).id;
      repaint();
      scheduleDrawNow();
    } else {
      stateRef.current.selectedId = null;
      repaint();
      scheduleDrawNow();
    }
  };

  const changeZoom = (delta: number) => {
    stateRef.current.zoom = clamp(stateRef.current.zoom + delta, MIN_ZOOM, MAX_ZOOM);
    repaint();
    scheduleDrawNow();
  };

  const selectedLocation = stateRef.current.selectedId
    ? locations.find((l) => l.id === stateRef.current.selectedId) ?? null
    : null;
  const meTravelTarget = stateRef.current.travelTarget;
  const targetLocation = meTravelTarget
    ? locations.find((l) => l.id === meTravelTarget) ?? null
    : null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full bg-bg overflow-hidden select-none',
        className,
      )}
      style={{ touchAction: 'none' }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        onMouseDown={(e) => onDown(e.clientX, e.clientY)}
        onMouseMove={(e) => onMove(e.clientX, e.clientY)}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={(e) => {
          if (e.touches.length === 1) onDown(e.touches[0].clientX, e.touches[0].clientY);
        }}
        onTouchMove={(e) => {
          if (e.touches.length === 1) onMove(e.touches[0].clientX, e.touches[0].clientY);
        }}
        onTouchEnd={onUp}
        onClick={handleCanvasClick}
      />

      {targetLocation && !selectedLocation && (
        <TravelTargetBar
          targetName={targetLocation.name}
          onClear={() => {
            if (!userId) return;
            clearTravelTarget.mutate();
          }}
        />
      )}

      {selectedLocation && (
        <LocationToast
          location={selectedLocation}
          isExplored={explored.has(selectedLocation.id)}
          isTravelTarget={selectedLocation.id === meTravelTarget}
          navigatorUnlocked={navigatorUnlocked}
          onClose={() => {
            stateRef.current.selectedId = null;
            repaint();
            scheduleDrawNow();
          }}
          onSetTarget={() => {
            if (!userId) return;
            setTravelTarget.mutate(selectedLocation.id, {
              onSuccess: () => {
                stateRef.current.travelTarget = selectedLocation.id;
                stateRef.current.selectedId = null;
                repaint();
                scheduleDrawNow();
              },
              onError: (err) => {
                hapticNotify('error');
                const message = err instanceof Error ? err.message : 'Не удалось назначить цель';
                pushNotification({ type: 'error', message });
              },
            });
          }}
          onClearTarget={() => {
            if (!userId) return;
            clearTravelTarget.mutate(undefined, {
              onSuccess: () => {
                stateRef.current.travelTarget = null;
                repaint();
                scheduleDrawNow();
              },
            });
          }}
        />
      )}

      <ZoomControls
        zoom={stateRef.current.zoom}
        onZoom={changeZoom}
        onCenter={() => centerOnPlayer(true)}
      />
    </div>
  );
}
